import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Plus } from 'lucide-react';
import { ALL_EMOJI_GROUPS, normaliseEmojiSearch } from './emojiGroups.js';

/**
 * Each picker supplies its own button, because a pick means something different
 * in each (toggle a reaction, insert text). It must return an element keyed by
 * the emoji, so a plain string or fragment is ruled out.
 */
type RenderChoice = (emoji: string) => ReactElement;

/** A picker's short default row, ending in the button that expands it to the full list. */
export function EmojiQuickRow({
  choices,
  expandLabel,
  renderChoice,
  onExpand,
}: {
  choices: string[];
  expandLabel: string;
  renderChoice: RenderChoice;
  onExpand: () => void;
}) {
  return (
    <>
      {choices.map(renderChoice)}
      <button
        type="button"
        aria-label={expandLabel}
        title={expandLabel}
        className="rounded-md px-1.5 py-1 text-base text-ink/60 hover:bg-black/5"
        onClick={onExpand}
      >
        <Plus size={16} aria-hidden />
      </button>
    </>
  );
}

/** The full, searchable emoji list that both the reaction palette and the comment composer expand to. */
export function EmojiGroupList({ renderChoice }: { renderChoice: RenderChoice }) {
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  // Expanding removes the button that had focus, so hand it to the search box
  // instead of letting it fall back to the body.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const needle = normaliseEmojiSearch(query);
  const groups = needle
    ? ALL_EMOJI_GROUPS.map((group) => ({
        ...group,
        emojis: group.emojis.filter((entry) => normaliseEmojiSearch(entry.name).includes(needle)),
      })).filter((group) => group.emojis.length > 0)
    : ALL_EMOJI_GROUPS;

  return (
    <>
      <div className="sticky top-0 z-10 bg-white p-1">
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            // The composer's list sits inside its form, where Enter in a lone
            // input would submit the comment.
            if (event.key === 'Enter') event.preventDefault();
          }}
          placeholder="Search emoji"
          aria-label="Search emoji"
          className="input h-8 text-sm"
        />
      </div>
      {groups.length === 0 ? (
        <p className="px-1.5 py-2 text-xs text-muted">No emoji found</p>
      ) : (
        groups.map((group) => (
          <section key={group.name} aria-label={group.name}>
            <h3 className="sticky top-10 bg-white px-1.5 py-1 text-[11px] font-semibold text-ink/50">
              {group.name}
            </h3>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(2rem,1fr))]">
              {group.emojis.map(({ emoji }) => renderChoice(emoji))}
            </div>
          </section>
        ))
      )}
    </>
  );
}
